import { GEOCODE_STATUSES, type GeocodeStatus } from '@promanage/core';

/**
 * "Which properties are missing from the map", as a link can carry it.
 *
 * ── WHY THIS IS A URL FILTER AND NOT A CHIP ──────────────────────────────────────────────────
 *
 * Nobody browses to it. The map page counts the properties it could not draw and says why —
 * "12 not on the map (3 address not found, …)" — and until now that count was a dead end: a
 * number with no way through to the twelve rows it is about. This is the other half of that
 * sentence, so it ARRIVES AS A LINK, exactly like `account` and `period` on the transactions
 * page. Adding a fifth occupancy chip for it would put a data-quality question in a row of
 * questions about tenancies, in front of every operator, every day, for a state that is usually
 * empty.
 *
 * `unmapped` is every one of them; the four named statuses are the map's own breakdown, so a
 * specific reason can be linked to directly. Only two of those are worth linking — see
 * `geocodeIsOperatorFixable` — but all four are accepted, because the URL is also something
 * people edit and a legible one should work.
 */
export const LOCATED_FILTERS = ['unmapped', ...GEOCODE_STATUSES] as const;
export type LocatedFilter = (typeof LOCATED_FILTERS)[number];

export function isLocatedFilter(value: string): value is LocatedFilter {
  return (LOCATED_FILTERS as readonly string[]).includes(value);
}

/**
 * Whether a property belongs in the filtered list.
 *
 * STRUCTURAL in its parameter, so the list row satisfies it without the row type having to be
 * imported here.
 *
 * `unmapped` asks about the STATUS rather than about `latitude is null`, and the two are the
 * same question: `properties_geocode_status_agrees` (docs/DATA_MODEL.md) makes `located` and
 * "has a coordinate" the same fact in both directions, so no row can disagree with itself. The
 * status is the one that also says why.
 */
export function matchesLocated(
  property: { geocodeStatus: GeocodeStatus },
  filter: LocatedFilter | undefined,
): boolean {
  if (filter === undefined) return true;
  if (filter === 'unmapped') return property.geocodeStatus !== 'located';
  return property.geocodeStatus === filter;
}

/**
 * What the list says it is showing, in the operator's words.
 *
 * Deliberately not `GEOCODE_STATUS_LABELS`. Those label a CHIP on one row — "Address not found"
 * describes that property. This labels a FILTERED SET, and has to read as a sentence about why
 * the other four hundred rows are missing.
 */
export function locatedFilterLabel(filter: LocatedFilter): string {
  switch (filter) {
    case 'unmapped':
      return 'not on the map';
    case 'located':
      return 'on the map';
    case 'pending':
      return 'awaiting an address lookup';
    case 'not_found':
      return 'whose address was not found';
    case 'ambiguous':
      return 'whose address matched more than one record';
    case 'failed':
      return 'whose address lookup was unavailable';
  }
}
